'use client';

import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

export type SortOrder = 'asc' | 'desc' | false;

type SortableItem = {
  name?: string;
  impressions?: string | number;
  clicks?: string | number;
  ctr?: string | number;
  cpm?: string | number;
  cpc?: string | number;
};

export function useProductSort<T extends SortableItem>(
  products: T[] | undefined
) {
  const [isSorting, setIsSorting] = useState(false);
  const [sortByOrderName, setSortByOrderName] = useState<SortOrder>(false);
  const [sortByOrderImpressions, setSortByOrderImpressions] =
    useState<SortOrder>(false);
  const [sortByOrderClicks, setSortByOrderClicks] = useState<SortOrder>(false);
  const [sortByOrderCTR, setSortByOrderCTR] = useState<SortOrder>(false);
  const [sortByOrderCPM, setSortByOrderCPM] = useState<SortOrder>(false);
  const [sortByOrderCPC, setSortByOrderCPC] = useState<SortOrder>(false);

  const [debouncedSortByOrderName, setDebouncedSortByOrderName] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderImpressions, setDebouncedSortByOrderImpressions] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderClicks, setDebouncedSortByOrderClicks] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderCTR, setDebouncedSortByOrderCTR] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderCPM, setDebouncedSortByOrderCPM] =
    useState<SortOrder>(false);
  const [debouncedSortByOrderCPC, setDebouncedSortByOrderCPC] =
    useState<SortOrder>(false);

  useDebounce(
    () => {
      setDebouncedSortByOrderName(sortByOrderName);
    },
    1,
    [sortByOrderName]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderImpressions(sortByOrderImpressions);
    },
    1,
    [sortByOrderImpressions]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderClicks(sortByOrderClicks);
    },
    1,
    [sortByOrderClicks]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderCTR(sortByOrderCTR);
    },
    1,
    [sortByOrderCTR]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderCPM(sortByOrderCPM);
    },
    1,
    [sortByOrderCPM]
  );

  useDebounce(
    () => {
      setDebouncedSortByOrderCPC(sortByOrderCPC);
    },
    1,
    [sortByOrderCPC]
  );

  const resetSortStates = (ignoreSet: (value: SortOrder) => void) => {
    const setters = {
      setSortByOrderName,
      setSortByOrderImpressions,
      setSortByOrderClicks,
      setSortByOrderCTR,
      setSortByOrderCPM,
      setSortByOrderCPC,
    };

    Object.values(setters).forEach(setter => {
      if (setter !== ignoreSet) {
        setter(false);
      }
    });
  };

  const toggleSort = (
    current: SortOrder,
    setSort: (value: SortOrder) => void
  ) => {
    setIsSorting(true);
    setTimeout(() => {
      resetSortStates(setSort);
      switch (current) {
        case 'asc':
          setSort('desc');
          break;
        case 'desc':
          setSort(false);
          break;
        default:
          setSort('asc');
          break;
      }
      setIsSorting(false);
    }, 300);
  };

  const sortedProducts = useMemo(() => {
    const productsList = products ?? [];

    if (debouncedSortByOrderName) {
      return [...productsList].sort((a, b) => {
        const nameA = (a.name ?? '').toLowerCase();
        const nameB = (b.name ?? '').toLowerCase();
        return debouncedSortByOrderName === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });
    }

    if (debouncedSortByOrderImpressions) {
      return [...productsList].sort((a, b) => {
        const impressionsA =
          typeof a.impressions === 'number'
            ? a.impressions
            : parseFloat(a.impressions?.replace(/\./g, '') ?? '0');
        const impressionsB =
          typeof b.impressions === 'number'
            ? b.impressions
            : parseFloat(b.impressions?.replace(/\./g, '') ?? '0');
        return debouncedSortByOrderImpressions === 'asc'
          ? impressionsA - impressionsB
          : impressionsB - impressionsA;
      });
    }

    if (debouncedSortByOrderClicks) {
      return [...productsList].sort((a, b) => {
        const clicksA =
          typeof a.clicks === 'number'
            ? a.clicks
            : parseFloat(a.clicks?.replace(/\./g, '') ?? '0');
        const clicksB =
          typeof b.clicks === 'number'
            ? b.clicks
            : parseFloat(b.clicks?.replace(/\./g, '') ?? '0');
        return debouncedSortByOrderClicks === 'asc'
          ? clicksA - clicksB
          : clicksB - clicksA;
      });
    }

    if (debouncedSortByOrderCTR) {
      return [...productsList].sort((a, b) => {
        const ctrA =
          typeof a.ctr === 'number'
            ? a.ctr
            : parseFloat((a.ctr ?? '0').toString().replace(',', '.'));
        const ctrB =
          typeof b.ctr === 'number'
            ? b.ctr
            : parseFloat((b.ctr ?? '0').toString().replace(',', '.'));
        return debouncedSortByOrderCTR === 'asc' ? ctrA - ctrB : ctrB - ctrA;
      });
    }

    if (debouncedSortByOrderCPM) {
      return [...productsList].sort((a, b) => {
        const cpmA =
          typeof a.cpm === 'number'
            ? a.cpm
            : parseFloat(
                (a.cpm ?? '0').toString().replace('.', '').replace(',', '.')
              );
        const cpmB =
          typeof b.cpm === 'number'
            ? b.cpm
            : parseFloat(
                (b.cpm ?? '0').toString().replace('.', '').replace(',', '.')
              );
        return debouncedSortByOrderCPM === 'asc' ? cpmA - cpmB : cpmB - cpmA;
      });
    }

    if (debouncedSortByOrderCPC) {
      return [...productsList].sort((a, b) => {
        const cpcA =
          typeof a.cpc === 'number'
            ? a.cpc
            : parseFloat(
                (a.cpc ?? '0').toString().replace('.', '').replace(',', '.')
              );
        const cpcB =
          typeof b.cpc === 'number'
            ? b.cpc
            : parseFloat(
                (b.cpc ?? '0').toString().replace('.', '').replace(',', '.')
              );
        return debouncedSortByOrderCPC === 'asc' ? cpcA - cpcB : cpcB - cpcA;
      });
    }

    return productsList;
  }, [
    products,
    debouncedSortByOrderName,
    debouncedSortByOrderImpressions,
    debouncedSortByOrderClicks,
    debouncedSortByOrderCTR,
    debouncedSortByOrderCPM,
    debouncedSortByOrderCPC,
  ]);

  return {
    sortedProducts,
    isSorting,
    sortByOrderName,
    sortByOrderImpressions,
    sortByOrderClicks,
    sortByOrderCTR,
    sortByOrderCPM,
    sortByOrderCPC,
    setSortByOrderName,
    setSortByOrderImpressions,
    setSortByOrderClicks,
    setSortByOrderCTR,
    setSortByOrderCPM,
    setSortByOrderCPC,
    toggleSort,
  };
}
